import ShapeDrillHoleGeneratorFactory from "@/Models/ShapeDrillHoleGeneratorFactory";
import isEmpty from "lodash/isEmpty";
import { CutoutTypes } from "@/Models/CutoutTypes";
import { Step, StepTypes } from '@/types/step';

const i18n = window.measurementSelectorVariables.i18n;

export default {
	state: (): object => ( {
		previewTransforms: {
			zoom: 1,
			rotation: 0,
			translation: [ 0, 0 ],
		},
		selectedMenuItem: 'rectangle',
		focusedElement: '',
		currentStepName: null,
		currentModalName: null,
		currentModalTriggerName: null,
		validSteps: [],
		disabledSteps: [],
		completedSteps: [],
		selectedOptions: {},
		defaultDimensions: true,
		priceExcVat: 0,
		priceIncVat: 0,
		steps: [
			{
				id: StepTypes.shapeSize,
				title: i18n.title.shape.long,
				shortTitle: i18n.title.shape.short,
				text: '',
				status: '',
				information: '',
			},
			{
				id: StepTypes.cutouts,
				title: i18n.title.cutouts.long,
				shortTitle: i18n.title.cutouts.short,
				text: '',
				status: '',
				information: '',
			},
			{
				id: StepTypes.drillholes,
				title: i18n.title.drillHoles.long,
				shortTitle: i18n.title.drillHoles.short,
				text: '',
				status: '',
				information: window.extendedProductInformation ? window.extendedProductInformation['drillholes'] : '',
			},
			{
				id: StepTypes.thickness,
				title: i18n.select_thickness,
				shortTitle: i18n.thickness,
				text: '',
				status: '',
				information: '',
			},
			{
				id: StepTypes.edgeFinish,
				title: i18n.edgeFinish.title.long,
				shortTitle: i18n.edgeFinish.title.short,
				text: '',
				status: '',
				information: window.extendedProductInformation ? window.extendedProductInformation['edge_finishing'] : '',
			},
			{
				id: StepTypes.material,
				title: i18n.material.selectMaterial,
				shortTitle: i18n.material.material,
				text: '',
				status: '',
				information: '',
			},
			{
				id: StepTypes.sideSelection,
				title: i18n.sideSelection.title.long,
				shortTitle: i18n.sideSelection.title.short,
				text: '',
				status: '',
				information: '',
			}
		]
	} ),
	mutations: {
		updateZoom: ( state, zoom ): void => state.previewTransforms.zoom = zoom,
		updateRotation: ( state, rotation ): void => state.previewTransforms.rotation = rotation,
		updateTranslation: ( state, translation ): void => state.previewTransforms.translation = translation,
		updateCurrentStepName: ( state, stepName ): void => state.currentStepName = stepName,
		updateCurrentModalName: ( state, modalName ): void => state.currentModalName = modalName,
		updateCurrentModalTriggerName: ( state, modalName ): void => state.currentModalTriggerName = modalName,
		updateMenuItem: ( state, menuItem ): void => state.selectedMenuItem = menuItem,
		updateFocusedElement: ( state, event ): void => {
			state.focusedElement = ( event.target ) ? event.target.id : event.id;
		},
		updateUsingDefaultDimensions: ( state, usesDefaults ): void => state.defaultDimensions = usesDefaults,
		updatePriceExcVat: ( state, priceExcVat ): void => state.priceExcVat = priceExcVat,
		updatePriceIncVat: ( state, priceIncVat ): void => state.priceIncVat = priceIncVat,
	},
	getters: {
		getStepById: (state, getters) => (id: StepTypes): object => {
			const step = state.steps.find((step: Step) => step.id === id);
			let currentStep = {};

			switch (id) {
				case StepTypes.shapeSize:
					currentStep = {
						text: `${getters.getShapeName}: ${getters.dimensionPreview}`,
						status: getters.hasDimensionValidatorErrors ? 'invalid' : 'completed',
					};
					break;

				case StepTypes.cutouts:
					currentStep = {
						text: getters.selectedCutoutsPreview,
						status: !getters.selectedCutoutsPreview ? '' : (getters.hasCutoutValidatorErrors ? 'invalid' : 'completed'),
					};
					break;

				case StepTypes.drillholes:
					currentStep = {
						text: getters.drillOption === 'none' ? '' : i18n.drillOptions[getters.drillOption],
						status: getters.drillOption === 'none' ? '' : (getters.hasDrillHoleValidatorErrors ? 'invalid' : 'completed'),
					};
					break;

				case StepTypes.thickness:
					const currentThickness = getters.currentProductThickness;
					currentStep = {
						text: `${currentThickness.thickness}mm`,
						status: currentThickness ? 'completed' : '',
					};
					break;

				case StepTypes.edgeFinish:
					currentStep = {
						text: i18n.edgeFinish.methods[getters.activeEdgeFinish],
						status: getters.activeEdgeFinish ? 'completed' : '',
					};
					break;

				case StepTypes.material:
					currentStep = {
						text: getters.currentProductMaterial?.title,
						status: getters.currentProductMaterial ? 'completed' : '',
					};
					break;

				case StepTypes.sideSelection:
					currentStep = {
						text: getters.sideLabel,
						status: getters.sideLabel ? 'completed' : '',
					};
					break;
			}

			return {
				...step,
				...currentStep,
			};
		},
		getShapeName( state, getters ): string {
			const { shape, presetShape } = getters;
			return presetShape !== '' ? i18n.shapePresets[presetShape] : i18n.shapeNames[shape];
		},
		getPriceExcVat: ( state ): number => state.priceExcVat,
		getPriceIncVat: ( state ): number => state.priceIncVat,
		isMediaMobileSize() : boolean {
			const media = window.matchMedia( "(max-width: 991px)" );

			return media.matches;
		},
		zoom: ( state ): number => state.previewTransforms.zoom,
		rotation: ( state ): number => state.previewTransforms.rotation,
		translation: ( state ): number[] => state.previewTransforms.translation,
		selectedMenuItem: ( state ): string => state.selectedMenuItem,
		currentStepName: ( state ): StepTypes => state.currentStepName,
		currentModalName: ( state ): string => state.currentModalName,
		currentModalTriggerName: ( state ): string => state.currentModalTriggerName,
		validSteps: ( state ): string[] => state.validSteps,
		completedSteps: ( state ): string[] => state.completedSteps,
		selectedOptions: ( state ): { [x: string]: string } => state.selectedOptions,
		focusedElement: ( state ): string => state.focusedElement,
		usesDefaultDimensions: ( state ): boolean => state.defaultDimensions,
		focusTrigger( state, getters ): string {
			if (getters.currentModalName === null) {
				return getters.currentModalTriggerName;
			} else {
				return null;
			}
		},
		shapeSelectorButtons( state, getters ): object[] {
			let buttons = [ 'rectangle', 'shapes', 'text', 'dxf' ];
			const i18n = window.measurementSelectorVariables.i18n;

			// Filter out rectangles as they have their own separate button.
			const otherShapeButtons = getters.enabledShapes.filter ( shape => shape !== 'rectangle' );

			// Filter out buttons that aren't enabled.
			buttons = buttons.filter( ( button ) => {
				if ( otherShapeButtons.length > 0 && button === 'shapes' ) {
					return true;
				}

				return getters.enabledShapes.includes( button );
			});

			return buttons.map((button) => {
				const selectionButton = {
					selection: button,
					handler: button,
					icon: button,
					reset: false,
					text: '',
					active: getters.selectedMenuItem === button,
				};

				switch (button) {
					case 'rectangle':
						return {
							...selectionButton,
							text: i18n.shapeNames.rectangle,
						};
					case 'dxf':
						return {
							...selectionButton,
							text: i18n.upload,
							reset: true,
						};
					case 'shapes':
						return {
							...selectionButton,
							text: i18n.shapes,
						};
					case 'text':
						return {
							...selectionButton,
							text: i18n.text.title,
						};
					default:
						return selectionButton;
				}
			});
		},
		drillHoleSelectorButtons( state, getters ): object[] {
			let buttons = [ 'none', 'edges', 'radial', 'grid', 'custom', 'preset' ];
			const i18n = window.measurementSelectorVariables.i18n;

			const {shape, model} = getters;
			const shapeDrillHoleGeneratorFactory = new ShapeDrillHoleGeneratorFactory();

			// Filter out buttons that aren't enabled.
			buttons = buttons.filter( ( button ) => {
				if ( ! shape ) {
					return false;
				}

				const { usePreset, presetGroup } = window.measurementSelectorVariables.drillHoleSettings;

				const shapeDrillHoleGenerator = shapeDrillHoleGeneratorFactory.getShapeDrillHoleGenerator( shape, model );
				const forcePreset = usePreset && 'fromEdge' in shapeDrillHoleGenerator && !isEmpty( presetGroup );

				const options = {
					'none': !forcePreset,
					'edges': !forcePreset && ( 'getCorners' in shapeDrillHoleGenerator || 'getAround' in shapeDrillHoleGenerator ),
					'radial': !forcePreset,
					'grid': !forcePreset,
					'custom': !forcePreset,
					'preset': forcePreset,
				};

				return (typeof options[button] !== 'undefined') ? options[button] : false;
			});

			return buttons.map((button) => {
				const selectionButton = {
					selection: button,
					handler: button,
					icon: button,
					text: i18n.drillOptions[ button ],
					active: getters.drillOption === button,
				};

				switch (button) {
					case 'none':
						return {
							...selectionButton,
							icon: 'cross',
						};

					default:
						return selectionButton;
				}
			});
		},
		cutoutSelectorButtons( state, getters ): object[] {
			let buttons = [ 'none', 'shape', 'text', 'dxf' ];
			const i18n = window.measurementSelectorVariables.i18n;

			// Filter out buttons that aren't enabled.
			buttons = buttons.filter( ( button ) => {
				const options = {
					[CutoutTypes.none]: true,
					[CutoutTypes.shape]: true,
					[CutoutTypes.text]: true,
					[CutoutTypes.dxf]: false,
				};

				return (typeof options[button] !== 'undefined') ? options[button] : false;
			});

			return buttons.map((button) => {
				const selectionButton = {
					selection: button,
					handler: button,
					icon: button,
					text: '',
					active: getters.cutoutType === button,
				};

				switch (button) {
					case 'none':
						return {
							...selectionButton,
							icon: 'cross',
							text: i18n.cutout.types.none,
						};
					case 'dxf':
						return {
							...selectionButton,
							text: i18n.cutout.types.upload,
						};
					case 'shape':
						return {
							...selectionButton,
							icon: 'shapes',
							text: i18n.cutout.types.shape,
						};
					case 'text':
						return {
							...selectionButton,
							text: i18n.cutout.types.text,
						};
					default:
						return selectionButton;
				}
			});
		},
	},
	actions: {}
};
